<template>
  <div class="tw:h-full tw:flex tw:flex-col">
    <!-- Product Selection View -->
    <template v-if="currentView === 'products'">
      <div class="tw:flex tw:flex-col tw:h-full">
        <div class="tw:p-4 tw:border-b tw:border-default-200">
          <div class="tw:flex tw:items-center tw:gap-3">
            <button
              class="tw:w-8 tw:h-8 tw:flex tw:items-center tw:justify-center tw:rounded-full hover:tw:bg-default-100 tw:transition-colors"
              @click="handleCloseProductsView"
            >
              <FontAwesomeIcon icon="fa-light fa-arrow-left" />
            </button>
            <div class="tw:flex tw:items-center tw:gap-2">
              <span class="tw:font-medium">{{ trans.products.drawer_title }}</span>
              <EdroneBadge
                :label="`${activeProductsSelection.length} / 10`"
                :type="activeProductsSelection.length >= 10 ? 'warning' : 'info'"
                size="small"
              />
            </div>
          </div>
        </div>

        <div class="tw:p-4 tw:border-b tw:border-default-200">
          <EdroneInput
            :focused="true"
            :placeholder="trans.products.search_placeholder"
            :value="productSearchQuery"
            @input="(val) => handleProductSearchInput(String(val))"
          >
            <template #icon>
              <FontAwesomeIcon icon="fa-light fa-magnifying-glass" />
            </template>
          </EdroneInput>
        </div>

        <div ref="productScrollContainer" class="tw:flex-1 tw:overflow-y-auto tw:p-4">
          <div v-if="isProductsLoading" class="tw:flex tw:items-center tw:justify-center tw:h-full">
            <EdroneLoading />
          </div>

          <div
            v-else-if="searchProducts.length === 0 && productSearchQuery.length > 0"
            class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:h-full tw:gap-2"
          >
            <FontAwesomeIcon class="tw:text-4xl tw:text-default-400" icon="fa-light fa-box-open" />
            <p class="tw:text-default-600 tw:text-center">{{ trans.products.empty_title }}</p>
            <p class="tw:text-sm tw:text-default-500 tw:text-center">{{ trans.products.empty_description }}</p>
          </div>

          <!-- Bestsellers when no search query -->
          <div v-else-if="searchProducts.length === 0 && productSearchQuery.length === 0" class="tw:flex tw:flex-col">
            <div v-if="isBestsellersLoading" class="tw:flex tw:items-center tw:justify-center tw:h-full">
              <EdroneLoading />
            </div>
            <template v-else-if="bestsellers.length > 0">
              <p class="tw:text-sm tw:font-medium tw:text-default-700 tw:mb-3">
                {{ trans.products.bestsellers_title }}
              </p>
              <div class="tw:grid tw:grid-cols-2 tw:gap-3">
                <div
                  v-for="product in bestsellers"
                  :key="product.trackerProductId"
                  :class="[
                    isProductSelected(product)
                      ? 'tw:border-blue-500 tw:bg-blue-000'
                      : 'tw:border-default-200 tw:bg-white hover:tw:border-default-400',
                    isMaxProductsSelected && !isProductSelected(product) ? 'tw:opacity-50 tw:cursor-not-allowed' : '',
                  ]"
                  class="tw:relative tw:cursor-pointer tw:rounded-lg tw:border tw:p-3 tw:transition-all"
                  @click="handleProductToggle(product)"
                >
                  <div class="tw:aspect-square tw:w-full tw:rounded tw:overflow-hidden tw:bg-default-100 tw:mb-2">
                    <img
                      v-if="product.img"
                      :alt="product.title"
                      :src="product.img"
                      class="tw:w-full tw:h-full tw:object-contain"
                    />
                    <div v-else class="tw:flex tw:items-center tw:justify-center tw:w-full tw:h-full">
                      <FontAwesomeIcon class="tw:text-default-400" icon="fa-light fa-image-slash" />
                    </div>
                  </div>
                  <p class="tw:text-sm tw:font-medium tw:line-clamp-2 tw:mb-1">{{ product.title }}</p>
                  <p class="tw:text-xs tw:text-default-600">
                    {{ formatNumberV2(product.price, { currency, style: "currency" }) }}
                  </p>
                  <div
                    v-if="isProductSelected(product)"
                    class="tw:absolute tw:top-2 tw:right-2 tw:w-5 tw:h-5 tw:bg-blue-500 tw:rounded-full tw:flex tw:items-center tw:justify-center"
                  >
                    <FontAwesomeIcon class="tw:text-white tw:text-xs" icon="fa-solid fa-check" />
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:h-full tw:gap-2">
              <FontAwesomeIcon class="tw:text-4xl tw:text-default-400" icon="fa-light fa-magnifying-glass" />
              <p class="tw:text-default-600 tw:text-center">{{ trans.products.search_placeholder }}</p>
            </div>
          </div>

          <div v-else class="tw:grid tw:grid-cols-2 tw:gap-3">
            <div
              v-for="product in searchProducts"
              :key="product.trackerProductId"
              :class="[
                isProductSelected(product)
                  ? 'tw:border-blue-500 tw:bg-blue-000'
                  : 'tw:border-default-200 tw:bg-white hover:tw:border-default-400',
                isMaxProductsSelected && !isProductSelected(product) ? 'tw:opacity-50 tw:cursor-not-allowed' : '',
              ]"
              class="tw:relative tw:cursor-pointer tw:rounded-lg tw:border tw:p-3 tw:transition-all"
              @click="handleProductToggle(product)"
            >
              <div class="tw:aspect-square tw:w-full tw:rounded tw:overflow-hidden tw:bg-default-100 tw:mb-2">
                <img
                  v-if="product.img"
                  :alt="product.title"
                  :src="product.img"
                  class="tw:w-full tw:h-full tw:object-contain"
                />
                <div v-else class="tw:flex tw:items-center tw:justify-center tw:w-full tw:h-full">
                  <FontAwesomeIcon class="tw:text-default-400" icon="fa-light fa-image-slash" />
                </div>
              </div>
              <p class="tw:text-sm tw:font-medium tw:line-clamp-2 tw:mb-1">{{ product.title }}</p>
              <p class="tw:text-xs tw:text-default-600">
                {{ formatNumberV2(product.price, { currency, style: "currency" }) }}
              </p>
              <div
                v-if="isProductSelected(product)"
                class="tw:absolute tw:top-2 tw:right-2 tw:w-5 tw:h-5 tw:bg-blue-500 tw:rounded-full tw:flex tw:items-center tw:justify-center"
              >
                <FontAwesomeIcon class="tw:text-white tw:text-xs" icon="fa-solid fa-check" />
              </div>
            </div>
          </div>

          <div v-if="isProductsFetchingNextPage" class="tw:flex tw:justify-center tw:mt-4 tw:mb-4">
            <EdroneLoading />
          </div>
        </div>

        <div v-if="activeProductsSelection.length > 0" class="tw:border-t tw:border-default-200 tw:p-4">
          <p class="tw:text-sm tw:font-medium tw:mb-2">
            {{ trans.products.selected_count }} ({{ activeProductsSelection.length }}/10)
          </p>
          <div class="tw:flex tw:flex-wrap tw:gap-2">
            <div
              v-for="product in activeProductsSelection"
              :key="product.trackerProductId"
              class="tw:flex tw:items-center tw:gap-1 tw:bg-blue-000 tw:border tw:border-blue-200 tw:rounded-full tw:px-2 tw:py-1"
            >
              <span class="tw:text-xs tw:max-w-24 tw:truncate">{{ product.title }}</span>
              <button class="tw:text-blue-500 hover:tw:text-blue-700" @click.stop="handleProductToggle(product)">
                <FontAwesomeIcon class="tw:text-xs" icon="fa-solid fa-xmark" />
              </button>
            </div>
          </div>
        </div>

        <div class="tw:border-t tw:border-default-200 tw:p-4 tw:flex tw:justify-end">
          <EdroneButton type="solid" @click="handleCloseProductsView">
            {{ trans.products.confirm_button }}
          </EdroneButton>
        </div>
      </div>
    </template>

    <!-- Main Prompt View -->
    <template v-else-if="chatMessages.length === 0">
      <div class="tw:flex-1 tw:flex tw:flex-col tw:items-center tw:justify-center tw:p-6">
        <div class="tw:w-full tw:max-w-md tw:flex tw:flex-col tw:gap-6">
          <div class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-2">
            <h1 class="tw:text-xl tw:font-bold tw:text-black tw:text-center">
              {{ trans.greeting.good_morning }},
              <span class="tw:capitalize">{{ EDRONE_CONTEXT.user.firstName }}</span>
              !
            </h1>
            <p class="tw:text-sm tw:text-default-700 tw:text-center">
              {{ trans.greeting.ready_to_create }}
            </p>
          </div>

          <!-- Usage loading state -->
          <div v-if="isUsageLoading" class="tw:flex tw:items-center tw:justify-center tw:py-8">
            <EdroneLoading />
          </div>

          <template v-else>
            <!-- Usage counter -->
            <div v-if="bannerUsage" class="tw:w-full">
              <div class="tw:flex tw:items-center tw:justify-between tw:mb-1.5">
                <span class="tw:text-xs tw:text-default-600">
                  {{ trans.usage.counter.replace("{used}", String(usedCount)).replace("{limit}", String(usageLimit)) }}
                </span>
                <span v-if="isAlmostAtLimit && !isLimitReached" class="tw:text-xs tw:text-yellow-600 tw:font-medium">
                  {{ trans.usage.almost_reached.replace("{remaining}", String(remainingCount)) }}
                </span>
              </div>
              <EdroneProgressBar :badge="false" :type="usageProgressType" :value="usagePercentage" />
            </div>

            <!-- Limit reached state -->
            <div
              v-if="isLimitReached"
              class="tw:flex tw:flex-col tw:items-center tw:gap-4 tw:p-6 tw:bg-default-50 tw:rounded-xl tw:border tw:border-default-200"
            >
              <div class="tw:w-14 tw:h-14 tw:rounded-full tw:bg-red-100 tw:flex tw:items-center tw:justify-center">
                <FontAwesomeIcon class="tw:text-2xl tw:text-red-500" icon="fa-light fa-ban" />
              </div>
              <div class="tw:text-center">
                <h2 class="tw:text-lg tw:font-bold tw:text-black tw:mb-2">{{ trans.usage.limit_reached_title }}</h2>
                <p v-if="!isPaying" class="tw:text-sm tw:text-default-700 tw:leading-relaxed">
                  {{
                    trans.usage.limit_reached_freemium_description
                      .replace("{limit}", String(usageLimit))
                      .replace("{paidLimit}", String(PAID_LIMIT))
                  }}
                </p>
                <p v-else class="tw:text-sm tw:text-default-700 tw:leading-relaxed">
                  {{ trans.usage.limit_reached_description.replace("{limit}", String(usageLimit)) }}
                </p>
              </div>
              <EdroneButton v-if="!isPaying" type="solid" @click="handleUpgradePlan">
                <template #left-icon>
                  <FontAwesomeIcon icon="fa-light fa-rocket" />
                </template>
                {{ trans.usage.upgrade_button }}
              </EdroneButton>
            </div>

            <!-- Attachment preview (above textarea) -->
            <div v-if="preview && !isLimitReached" class="tw:relative tw:inline-block tw:w-fit tw:mx-auto">
              <img
                :src="preview"
                alt="Attachment preview"
                class="tw:max-h-32 tw:rounded-lg tw:border tw:border-default-200"
              />
              <button
                v-if="!isGenerating"
                class="tw:absolute tw:-top-2 tw:-right-2 tw:bg-black tw:text-white tw:rounded-full tw:w-6 tw:h-6 tw:flex tw:items-center tw:justify-center hover:tw:bg-gray-800 tw:transition-colors"
                @click="removeAttachment"
              >
                <FontAwesomeIcon class="tw:text-xs" icon="fa-solid fa-xmark" />
              </button>
            </div>

            <!-- Selected Products Preview -->
            <div
              v-if="selectedProducts.length > 0 && !isLimitReached"
              class="tw:flex tw:flex-wrap tw:gap-2 tw:justify-center"
            >
              <div
                v-for="product in selectedProducts"
                :key="product.trackerProductId"
                class="tw:flex tw:items-center tw:gap-1 tw:bg-blue-000 tw:border tw:border-blue-200 tw:rounded-full tw:px-2 tw:py-1"
              >
                <span class="tw:text-xs tw:max-w-20 tw:truncate">{{ product.title }}</span>
                <button
                  :disabled="isGenerating"
                  class="tw:text-blue-500 hover:tw:text-blue-700"
                  @click="handleRemoveProduct(product)"
                >
                  <FontAwesomeIcon class="tw:text-xs" icon="fa-solid fa-xmark" />
                </button>
              </div>
            </div>

            <!-- Banner Generation Form -->
            <div v-if="!isLimitReached" class="tw:flex tw:flex-col tw:gap-4">
              <div class="tw:flex tw:flex-col tw:gap-3">
                <!-- Top row: Buttons left, Size selector right -->
                <div class="tw:flex tw:items-center tw:justify-between tw:gap-2">
                  <div class="tw:flex tw:flex-wrap tw:gap-2 tw:items-center">
                    <!-- Attach Image Button -->
                    <div v-if="!attachmentPreview && !attachmentUrl" class="ai-banner-tooltip-wrapper">
                      <EdroneButton :disabled="isGenerating" size="medium" type="outline" @click="triggerFileInput">
                        <template #left-icon>
                          <FontAwesomeIcon icon="fa-light fa-paperclip" />
                        </template>
                        {{ trans.form.attach_button }}
                      </EdroneButton>
                      <div class="ai-banner-tooltip">
                        {{ trans.form.attach_button_tooltip }}
                      </div>
                    </div>

                    <!-- Choose Products Button -->
                    <EdroneButton
                      :disabled="isGenerating"
                      size="medium"
                      type="outline"
                      @click="currentView = 'products'"
                    >
                      <template #left-icon>
                        <FontAwesomeIcon icon="fa-light fa-bag-shopping" />
                      </template>
                      {{ trans.form.products_button }}
                      <template v-if="selectedProducts.length > 0" #right-icon>
                        <EdroneBadge :label="String(selectedProducts.length)" size="small" type="info" />
                      </template>
                    </EdroneButton>
                  </div>

                  <!-- Size selector -->
                  <div ref="sizeDropdownRef" class="tw:relative">
                    <EdroneButton
                      :disabled="isGenerating"
                      size="medium"
                      type="outline"
                      @click="showSizeDropdown = !showSizeDropdown"
                    >
                      <template #left-icon>
                        <FontAwesomeIcon icon="fa-light fa-expand" />
                      </template>
                      {{ trans.size[selectedSize] }}
                      <template #right-icon>
                        <FontAwesomeIcon class="tw:text-[10px]" icon="fa-solid fa-chevron-down" />
                      </template>
                    </EdroneButton>
                    <div
                      v-if="showSizeDropdown"
                      class="tw:absolute tw:top-full tw:right-0 tw:mt-1 tw:bg-white tw:border tw:border-default-200 tw:rounded-lg tw:shadow-lg tw:z-20 tw:py-1 tw:min-w-32"
                    >
                      <button
                        v-for="size in BANNER_SIZES"
                        :key="size.id"
                        :class="selectedSize === size.id ? 'tw:text-blue-500 tw:font-medium' : 'tw:text-black'"
                        class="tw:w-full tw:text-left tw:px-3 tw:py-2 tw:text-sm tw:bg-white tw:border-b tw:border-default-100 tw:last:border-b-0"
                        @click="selectSize(size.id)"
                      >
                        {{ trans.size[size.id] }}
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Textarea with history button inside -->
                <div class="tw:relative">
                  <EdroneTextarea
                    :disabled="isGenerating"
                    :focused="true"
                    :maxlength="500"
                    :placeholder="trans.form.placeholder"
                    :rows="5"
                    :value="prompt"
                    class="ai-banner-textarea"
                    @enter="handleSubmitForm"
                    @input="prompt = $event"
                  />
                  <!-- History Icon Button inside textarea -->
                  <div ref="historyDropdownRef" class="tw:absolute tw:bottom-8 tw:right-3 tw:z-10">
                    <button
                      :disabled="isGenerating"
                      :title="trans.form.history_button"
                      class="tw:w-8 tw:h-8 tw:flex tw:items-center tw:justify-center tw:rounded-lg tw:bg-white tw:border tw:border-default-300 tw:text-default-700 hover:tw:border-default-400 tw:transition-colors disabled:tw:opacity-50 tw:shadow-sm"
                      @click="toggleHistoryDropdown"
                    >
                      <FontAwesomeIcon class="tw:text-sm" icon="fa-light fa-clock-rotate-left" />
                    </button>
                    <div
                      v-if="showHistoryDropdown"
                      class="tw:absolute tw:bottom-full tw:right-0 tw:mb-1 tw:w-72 tw:bg-white tw:border tw:border-default-200 tw:rounded-lg tw:shadow-lg tw:z-20 tw:max-h-48 tw:overflow-y-auto"
                    >
                      <div>
                        <div
                          v-if="promptHistory.length <= 0"
                          class="tw:w-full tw:px-3 tw:py-2 tw:bg-gray-50 tw:text-sm tw:text-gray-500"
                        >
                          {{ trans.history.no_history }}
                        </div>
                        <div
                          v-for="(item, index) in promptHistory"
                          :key="index"
                          class="tw:w-full tw:cursor-pointer tw:text-left tw:px-3 tw:py-2 tw:text-sm tw:hover:bg-gray-50 tw:border-b tw:border-gray-100 tw:last:border-b-0"
                          @click="selectPromptFromHistory(item)"
                        >
                          <div class="tw:truncate tw:mb-1">{{ item.prompt }}</div>
                          <div class="tw:text-xs tw:text-gray-500">
                            {{ new Date(item.timestamp).toLocaleDateString("pl-PL") }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Generate Button -->
                <EdroneButton
                  :disabled="prompt.length === 0 || isLimitReached"
                  :loading="isGenerating"
                  class="tw:w-full"
                  type="ai-solid"
                  @click="handleSubmitForm"
                >
                  {{ trans.form.generate_button }}
                  <template #right-icon>
                    <EdroneAiSparklesIcon />
                  </template>
                </EdroneButton>

                <!-- Dev controls (local only) -->
                <div v-if="isLocal" class="tw:flex tw:items-center tw:justify-center tw:gap-4">
                  <!-- Model selector -->
                  <div ref="modelDropdownRef" class="tw:relative">
                    <button
                      :disabled="isGenerating"
                      class="tw:flex tw:items-center tw:gap-2 tw:text-xs tw:text-default-500 hover:tw:text-default-700 tw:transition-colors disabled:tw:opacity-50 tw:bg-transparent tw:border-0"
                      @click="showModelDropdown = !showModelDropdown"
                    >
                      <FontAwesomeIcon icon="fa-light fa-microchip-ai" />
                      {{ getSelectedModelLabel() }}
                      <FontAwesomeIcon class="tw:text-[8px]" icon="fa-solid fa-chevron-down" />
                    </button>
                    <div
                      v-if="showModelDropdown"
                      class="tw:absolute tw:top-full tw:left-1/2 tw:-translate-x-1/2 tw:mt-1 tw:bg-white tw:border tw:border-default-200 tw:rounded-lg tw:shadow-lg tw:z-20 tw:py-1 tw:min-w-36"
                    >
                      <button
                        v-for="model in BANNER_MODELS"
                        :key="model.id"
                        :class="selectedModel === model.id ? 'tw:text-blue-500 tw:font-medium' : 'tw:text-black'"
                        class="tw:w-full tw:text-left tw:px-3 tw:py-2 tw:text-sm tw:bg-transparent tw:border-0 tw:border-b tw:border-default-100 tw:last:border-b-0"
                        @click="selectModel(model.id)"
                      >
                        {{ model.label }}
                      </button>
                    </div>
                  </div>

                  <!-- Analysis mode selector -->
                  <div ref="analysisDropdownRef" class="tw:relative">
                    <button
                      :disabled="isGenerating"
                      class="tw:flex tw:items-center tw:gap-2 tw:text-xs tw:text-default-500 hover:tw:text-default-700 tw:transition-colors disabled:tw:opacity-50 tw:bg-transparent tw:border-0"
                      @click="showAnalysisDropdown = !showAnalysisDropdown"
                    >
                      <FontAwesomeIcon icon="fa-light fa-brain-circuit" />
                      {{ ANALYSIS_MODES.find((m) => m.id === selectedAnalysisMode)?.label }}
                      <FontAwesomeIcon class="tw:text-[8px]" icon="fa-solid fa-chevron-down" />
                    </button>
                    <div
                      v-if="showAnalysisDropdown"
                      class="tw:absolute tw:top-full tw:left-1/2 tw:-translate-x-1/2 tw:mt-1 tw:bg-white tw:border tw:border-default-200 tw:rounded-lg tw:shadow-lg tw:z-20 tw:py-1 tw:min-w-36"
                    >
                      <button
                        v-for="mode in ANALYSIS_MODES"
                        :key="mode.id"
                        :class="selectedAnalysisMode === mode.id ? 'tw:text-blue-500 tw:font-medium' : 'tw:text-black'"
                        class="tw:w-full tw:text-left tw:px-3 tw:py-2 tw:text-sm tw:bg-transparent tw:border-0 tw:border-b tw:border-default-100 tw:last:border-b-0"
                        @click="selectAnalysisMode(mode.id)"
                      >
                        {{ mode.label }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- Divider section -->
          <div class="tw:flex tw:flex-col tw:gap-4 tw:mt-4">
            <div class="tw:flex tw:items-center tw:gap-3">
              <div class="tw:flex-1 tw:h-px tw:bg-default-200"></div>
              <p class="tw:text-xs tw:text-default-600">{{ trans.history.recent_banners }}</p>
              <div class="tw:flex-1 tw:h-px tw:bg-default-200"></div>
            </div>

            <!-- Recent banners -->
            <div v-if="isHistoryLoading" class="tw:overflow-x-auto ai-banner-history-scroll">
              <div class="tw:flex tw:gap-3 tw:justify-start tw:pb-2" style="min-width: fit-content">
                <div v-for="index in 3" :key="index" class="tw:flex-shrink-0" style="height: 10rem; width: 17.8rem">
                  <EdroneSkeletonLoader class="tw:rounded-lg" />
                </div>
              </div>
            </div>
            <div
              v-else-if="history && history.length"
              class="tw:relative tw:group/carousel"
              @mouseenter="updateScrollArrows"
            >
              <button
                v-show="canScrollLeft"
                class="tw:absolute tw:left-0 tw:top-1/2 tw:-translate-y-1/2 tw:z-10 tw:w-8 tw:h-8 tw:flex tw:items-center tw:justify-center tw:rounded-full tw:bg-white tw:border tw:border-default-200 tw:shadow-md tw:opacity-0 tw:group-hover/carousel:opacity-100 tw:transition-opacity hover:tw:bg-default-50"
                @click="scrollHistory('left')"
              >
                <FontAwesomeIcon class="tw:text-sm tw:text-default-700" icon="fa-solid fa-chevron-left" />
              </button>
              <button
                v-show="canScrollRight"
                class="tw:absolute tw:right-0 tw:top-1/2 tw:-translate-y-1/2 tw:z-10 tw:w-8 tw:h-8 tw:flex tw:items-center tw:justify-center tw:rounded-full tw:bg-white tw:border tw:border-default-200 tw:shadow-md tw:opacity-0 tw:group-hover/carousel:opacity-100 tw:transition-opacity hover:tw:bg-default-50"
                @click="scrollHistory('right')"
              >
                <FontAwesomeIcon class="tw:text-sm tw:text-default-700" icon="fa-solid fa-chevron-right" />
              </button>
              <div
                ref="historyScrollRef"
                class="tw:overflow-x-auto ai-banner-history-scroll"
                @scroll="updateScrollArrows"
              >
                <div class="tw:flex tw:gap-3 tw:justify-start tw:pb-2" style="min-width: fit-content">
                  <div
                    v-for="(item, index) in history"
                    :key="index"
                    class="tw:relative tw:cursor-pointer tw:group"
                    @click="handleSelectBanner(item)"
                  >
                    <AiBannerLazyImage
                      :alt="trans.download.alt_previous"
                      :src="item"
                      class="tw:group-hover:shadow-md tw:group-hover:scale-105"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Chat response area (shows after generation and replaces main content) -->
    <div v-if="chatMessages.length > 0" class="tw:flex-1 tw:flex tw:flex-col tw:overflow-hidden">
      <!-- Feedback notification banner -->
      <div class="tw:bg-default-100 tw:border-b tw:border-default-200 tw:px-4 tw:py-3">
        <div class="tw:flex tw:items-center tw:justify-center tw:gap-2 tw:text-sm">
          <FontAwesomeIcon icon="fa-light fa-sparkles" />
          <span>
            {{ trans.feedback.notification_text }}
            <FontAwesomeIcon class="tw:mx-0.5" icon="fa-light fa-thumbs-up" />
            <FontAwesomeIcon class="tw:mx-0.5" icon="fa-light fa-thumbs-down" />
            {{ trans.feedback.notification_icons }}
          </span>
        </div>
      </div>

      <!-- Scrollable chat messages area -->
      <div class="tw:flex-1 tw:overflow-y-auto tw:bg-white">
        <div class="tw:p-4">
          <div class="tw:flex tw:flex-col tw:gap-3">
            <AiBannerChatMessage
              v-for="(message, index) in chatMessages"
              :key="index"
              :message="message"
              @iterate="handleIterateFromMessage"
            />

            <!-- Loading message -->
            <AiBannerChatMessage
              v-if="isGenerating || isModifying"
              :message="{
                type: 'agent',
                content: isGenerating ? trans.messages.generating : trans.messages.modifying,
                isLoading: true,
              }"
            />
          </div>
        </div>
      </div>

      <!-- Fixed input section at bottom -->
      <div :style="modifyInputSectionStyle" class="tw:border-t tw:border-default-200 tw:bg-gray-50 tw:p-4">
        <!-- Limit reached in chat view -->
        <div v-if="isLimitReached" class="tw:flex tw:flex-col tw:items-center tw:gap-3 tw:py-2">
          <div class="tw:flex tw:items-center tw:gap-2 tw:text-red-600">
            <FontAwesomeIcon icon="fa-light fa-ban" />
            <span class="tw:text-sm tw:font-medium">{{ trans.usage.limit_reached_title }}</span>
          </div>
          <p v-if="!isPaying" class="tw:text-xs tw:text-default-600 tw:text-center">
            {{
              trans.usage.limit_reached_freemium_description
                .replace("{limit}", String(usageLimit))
                .replace("{paidLimit}", String(PAID_LIMIT))
            }}
          </p>
          <p v-else class="tw:text-xs tw:text-default-600 tw:text-center">
            {{ trans.usage.limit_reached_description.replace("{limit}", String(usageLimit)) }}
          </p>
          <div class="tw:flex tw:gap-2">
            <EdroneButton type="outline" @click="handleStartOver">
              <template #left-icon>
                <FontAwesomeIcon icon="fa-solid fa-plus" />
              </template>
              {{ trans.form.start_over_button }}
            </EdroneButton>
            <EdroneButton v-if="!isPaying" type="solid" @click="handleUpgradePlan">
              <template #left-icon>
                <FontAwesomeIcon icon="fa-light fa-rocket" />
              </template>
              {{ trans.usage.upgrade_button }}
            </EdroneButton>
          </div>
        </div>

        <template v-else>
          <!-- Modify attachment preview -->
          <div v-if="modifyPreview" class="tw:relative tw:inline-block tw:w-fit tw:mb-3">
            <img
              :src="modifyPreview"
              alt="Attachment preview"
              class="tw:max-h-20 tw:rounded-lg tw:border tw:border-default-200"
            />
            <button
              v-if="!isGenerating && !isModifying"
              class="tw:absolute tw:-top-2 tw:-right-2 tw:bg-black tw:text-white tw:rounded-full tw:w-5 tw:h-5 tw:flex tw:items-center tw:justify-center hover:tw:bg-gray-800 tw:transition-colors"
              @click="removeModifyAttachment"
            >
              <FontAwesomeIcon class="tw:text-xs" icon="fa-solid fa-xmark" />
            </button>
          </div>

          <!-- Modify selected products preview -->
          <div v-if="modifySelectedProducts.length > 0" class="tw:flex tw:flex-wrap tw:gap-2 tw:mb-3">
            <div
              v-for="product in modifySelectedProducts"
              :key="product.trackerProductId"
              class="tw:flex tw:items-center tw:gap-1 tw:bg-blue-000 tw:border tw:border-blue-200 tw:rounded-full tw:px-2 tw:py-1"
            >
              <span class="tw:text-xs tw:max-w-20 tw:truncate">{{ product.title }}</span>
              <button
                :disabled="isGenerating || isModifying"
                class="tw:text-blue-500 hover:tw:text-blue-700 disabled:tw:opacity-50"
                @click="handleRemoveModifyProduct(product)"
              >
                <FontAwesomeIcon class="tw:text-xs" icon="fa-solid fa-xmark" />
              </button>
            </div>
          </div>

          <!-- Modify action buttons -->
          <div class="tw:flex tw:flex-wrap tw:gap-2 tw:items-center tw:mb-3">
            <!-- Attach Image Button -->
            <div v-if="!modifyAttachmentPreview && !modifyAttachmentUrl" class="ai-banner-tooltip-wrapper">
              <EdroneButton
                :disabled="isGenerating || isModifying"
                size="medium"
                type="outline"
                @click="triggerModifyFileInput"
              >
                <template #left-icon>
                  <FontAwesomeIcon icon="fa-light fa-paperclip" />
                </template>
                {{ trans.form.attach_button }}
              </EdroneButton>
              <div class="ai-banner-tooltip">
                {{ trans.form.attach_button_tooltip }}
              </div>
            </div>

            <!-- Choose Products Button -->
            <EdroneButton
              :disabled="isGenerating || isModifying"
              size="medium"
              type="outline"
              @click="openModifyProductsView"
            >
              <template #left-icon>
                <FontAwesomeIcon icon="fa-light fa-bag-shopping" />
              </template>
              {{ trans.form.products_button }}
              <template v-if="modifySelectedProducts.length > 0" #right-icon>
                <EdroneBadge :label="String(modifySelectedProducts.length)" size="small" type="info" />
              </template>
            </EdroneButton>
          </div>

          <EdroneTextarea
            :disabled="isGenerating || isModifying"
            :maxlength="500"
            :placeholder="trans.messages.modify_placeholder"
            :rows="4"
            :value="iterationPrompt"
            class="tw:w-full tw:mb-3"
            style="font-size: 1rem !important"
            @enter="handleIterate"
            @input="iterationPrompt = $event"
          />
          <div class="tw:flex tw:gap-2 tw:justify-between">
            <EdroneButton :disabled="isGenerating || isModifying" type="outline" @click="handleStartOver">
              <template #left-icon>
                <FontAwesomeIcon icon="fa-solid fa-plus" />
              </template>
              {{ trans.form.start_over_button }}
            </EdroneButton>
            <EdroneButton
              :disabled="!iterationPrompt || !iterationPrompt.trim() || isGenerating || isModifying || !chatId"
              :loading="isModifying"
              type="ai-solid"
              @click="handleIterate"
            >
              <template #left-icon>
                <EdroneAiSparklesIcon />
              </template>
              {{ trans.form.iterate_button }}
            </EdroneButton>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, onUnmounted } from "vue";
import { useMutation, useQuery, useInfiniteQuery } from "@tanstack/vue-query";
import { useInfiniteScroll } from "@vueuse/core";
import { debounce } from "lodash";
import EdroneButton from "edrone-components/EdroneButton.vue";
import EdroneLoading from "edrone-components/EdroneLoading.vue";
import EdroneSkeletonLoader from "edrone-components/EdroneSkeletonLoader.vue";
import EdroneTextarea from "edrone-components/EdroneTextarea.vue";
import EdroneAiSparklesIcon from "edrone-components/EdroneAiSparklesIcon.vue";
import EdroneBadge from "edrone-components/EdroneBadge/EdroneBadge.vue";
import EdroneInput from "edrone-components/EdroneInput.vue";
import EdroneProgressBar from "edrone-components/EdroneProgressBar.vue";
import { generateAiBanner, getBannerUsage, getHistory, modifyAiBanner } from "vue-app/api/AiBannerService";
import { getDataScienceSearchProducts } from "vue-app/api/DataScienceSearchService";
import {
  getTrackerCategoryProductsTableV2,
  TRACKER_CATEGORY_PRODUCT_TABLE_SORT_KEY,
} from "vue-app/api/CategoryService";
import { SORT_DIRECTION } from "vue-app/types/enums";
import type { DTODataScienceSearchProductsResponse } from "vue-app/api/dto";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { getRandomPrompt, getPromptHistory, savePromptToHistory, type PromptHistoryItem } from "./AiBanner.constants";
import AiBannerChatMessage from "./AiBannerChatMessage.vue";
import AiBannerLazyImage from "./AiBannerLazyImage.vue";
import { useAiBannerStore } from "vue-app/components/AiBanner/AiBanner.store.ts";
import { storeToRefs } from "pinia";
import { formatNumberV2 } from "vue-app/utils/functions";

export interface AiBannerProduct {
  trackerProductId: number;
  appIdProductId: string;
  price: number;
  title: string;
  img: string | null;
}

type BannerSize = "wide" | "square" | "vertical" | "landscape";

const BANNER_SIZES: { id: BannerSize; width: number; height: number }[] = [
  { id: "wide", width: 1200, height: 628 },
  { id: "square", width: 1080, height: 1080 },
  { id: "vertical", width: 1080, height: 1920 },
  { id: "landscape", width: 1920, height: 1080 },
];

type BannerModel = { id: number; label: string };

const BANNER_MODELS: BannerModel[] = [
  { id: 2, label: "Gemini 3.0 Pro" },
  { id: 1, label: "Gemini 2.5 Flash" },
];

const isLocal = window.EDRONE_CONTEXT?.isLocal ?? false;

type ChatMessage = {
  type: "user" | "agent";
  content: string;
  imageUrl?: string;
  isLoading?: boolean;
  isError?: boolean;
  prompt?: string;
  modelId?: number;
  attachmentUrl?: string | null;
  products?: Array<{ title: string; img?: string }>;
};

interface Props {
  isOpen: boolean;
  downloadOnSelect?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  downloadOnSelect: true,
});

const prompt = ref(getRandomPrompt());
const iterationPrompt = ref("");
const selectedModel = ref(2);
const promptHistory = ref<PromptHistoryItem[]>([]);
const showHistoryDropdown = ref(false);
const chatMessages = ref<ChatMessage[]>([]);
const chatId = ref<string | null>(null);
const attachmentFile = ref<File | null>(null);
const attachmentPreview = ref<string | null>(null);
const attachmentUrl = ref<string | null>(null);
const modifyAttachmentFile = ref<File | null>(null);
const modifyAttachmentPreview = ref<string | null>(null);
const modifyAttachmentUrl = ref<string | null>(null);
const lastUsedPrompt = ref("");
const lastUsedModel = ref(2);
const lastUsedAttachmentUrl = ref<string | null>(null);
const selectedProducts = ref<AiBannerProduct[]>([]);
const modifySelectedProducts = ref<AiBannerProduct[]>([]);
const isModifyProductsView = ref(false);
const currentView = ref<"prompt" | "products">("prompt");
const productSearchQuery = ref("");
const productScrollContainer = ref<HTMLElement | null>(null);
const currency = window?.EDRONE_CONTEXT?.user.currency || "USD";
const selectedSize = ref<BannerSize>("wide");
const showSizeDropdown = ref(false);
const sizeDropdownRef = ref<HTMLElement | null>(null);
const historyDropdownRef = ref<HTMLElement | null>(null);
const showModelDropdown = ref(false);
const modelDropdownRef = ref<HTMLElement | null>(null);
const showAnalysisDropdown = ref(false);
const analysisDropdownRef = ref<HTMLElement | null>(null);
const historyScrollRef = ref<HTMLElement | null>(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(true);

type AnalysisMode = "enabled" | "disabled";
const ANALYSIS_MODES: { id: AnalysisMode; label: string }[] = [
  { id: "enabled", label: "AI Analysis: ON" },
  { id: "disabled", label: "AI Analysis: OFF" },
];
const selectedAnalysisMode = ref<AnalysisMode>("enabled");

const store = useAiBannerStore();
const { hasNewContent } = storeToRefs(store);

const trans = window._trans._branding.aiBanner;
const isPaying = window.EDRONE_CONTEXT?.tracker?.isPaying ?? false;
const PAID_LIMIT = 100;
const FREEMIUM_LIMIT = 20;

const {
  data: bannerUsage,
  refetch: refetchUsage,
  isLoading: isUsageLoading,
} = useQuery({
  queryKey: ["bannerUsage"],
  queryFn: getBannerUsage,
  enabled: computed(() => props.isOpen),
  staleTime: 1000 * 60 * 2,
});

const usedCount = computed(() => bannerUsage.value?.usedCount ?? 0);
const usageLimit = computed(() => bannerUsage.value?.limit ?? (isPaying ? PAID_LIMIT : FREEMIUM_LIMIT));
const remainingCount = computed(() => Math.max(0, usageLimit.value - usedCount.value));
const isLimitReached = computed(() => usedCount.value >= usageLimit.value);
const usagePercentage = computed(() => Math.min(100, Math.round((usedCount.value / usageLimit.value) * 100)));
const isAlmostAtLimit = computed(() => remainingCount.value > 0 && remainingCount.value <= 5);
const usageProgressType = computed(() => {
  if (isLimitReached.value) return "error";
  if (isAlmostAtLimit.value) return "warning";
  return "info";
});

const preview = computed(() => (attachmentPreview.value ? attachmentPreview.value : attachmentUrl.value || null));
const modifyPreview = computed(() =>
  modifyAttachmentPreview.value ? modifyAttachmentPreview.value : modifyAttachmentUrl.value || null
);

const modifyInputSectionStyle = computed(() => {
  if (isLimitReached.value) {
    return {};
  }

  const BASE_HEIGHT_REM = 17.5;
  const ACTION_BUTTONS_HEIGHT_REM = 2.875;
  const ATTACHMENT_PREVIEW_HEIGHT_REM = 6;
  const PRODUCTS_PREVIEW_HEIGHT_REM = 2.5;

  let totalHeight = BASE_HEIGHT_REM + ACTION_BUTTONS_HEIGHT_REM;

  if (modifyPreview.value) {
    totalHeight += ATTACHMENT_PREVIEW_HEIGHT_REM;
  }

  if (modifySelectedProducts.value.length > 0) {
    totalHeight += PRODUCTS_PREVIEW_HEIGHT_REM;
  }

  return { height: `${totalHeight}rem` };
});

const selectSize = (sizeId: BannerSize) => {
  selectedSize.value = sizeId;
  showSizeDropdown.value = false;
};

const selectModel = (modelId: number) => {
  selectedModel.value = modelId;
  showModelDropdown.value = false;
};

const selectAnalysisMode = (modeId: AnalysisMode) => {
  selectedAnalysisMode.value = modeId;
  showAnalysisDropdown.value = false;
};

const getSelectedModelLabel = () => {
  return BANNER_MODELS.find((m) => m.id === selectedModel.value)?.label ?? "Gemini 3.0 Pro";
};

const { mutate: generate, isPending: isGenerating } = useMutation({
  mutationFn: generateAiBanner,
  onSuccess: (data) => {
    chatMessages.value.push({
      type: "agent",
      content: trans.messages.here_is_banner,
      imageUrl: data.url,
      prompt: lastUsedPrompt.value,
      modelId: lastUsedModel.value,
      attachmentUrl: lastUsedAttachmentUrl.value,
    });
    chatId.value = data.chatId;
    store.setLastGeneratedBannerUrl(data.url);
    refetchHistory();
    refetchUsage();
    if (!props.isOpen) {
      hasNewContent.value = true;
      window.showToast(
        {
          message: trans.notification.banner_ready,
          desc: trans.notification.click_to_view,
          link: {
            text: trans.notification.open_drawer,
            url: "#open-ai-banner",
          },
        },
        "success"
      );
    }
  },
  onError: (error: unknown) => {
    const axiosError = error as { response?: { data?: { message?: string }; status?: number } };
    const errorMessage = axiosError?.response?.data?.message || "";
    const isContentPolicy =
      axiosError?.response?.status === 400 &&
      (errorMessage.toLowerCase().includes("content policy") ||
        errorMessage.toLowerCase().includes("blocked") ||
        errorMessage.toLowerCase().includes("copyright"));

    chatMessages.value.push({
      type: "agent",
      content: isContentPolicy ? trans.errors.content_policy : trans.errors.generate_failed_chat,
      isError: true,
    });
    console.error("Failed to generate banner:", error);
  },
});

const { mutate: modify, isPending: isModifying } = useMutation({
  mutationFn: modifyAiBanner,
  onSuccess: (data) => {
    chatMessages.value.push({
      type: "agent",
      content: trans.messages.here_is_updated,
      imageUrl: data.url,
      prompt: lastUsedPrompt.value,
      modelId: lastUsedModel.value,
      attachmentUrl: lastUsedAttachmentUrl.value,
    });
    chatId.value = data.chatId;
    store.setLastGeneratedBannerUrl(data.url);
    iterationPrompt.value = "";
    refetchHistory();
    refetchUsage();
    if (!props.isOpen) {
      hasNewContent.value = true;
      window.showToast(
        {
          message: trans.notification.banner_ready,
          desc: trans.notification.click_to_view,
          link: {
            text: trans.notification.open_drawer,
            url: "#open-ai-banner",
          },
        },
        "success"
      );
    }
  },
  onError: (error: unknown) => {
    const axiosError = error as { response?: { data?: { message?: string }; status?: number } };
    const errorMessage = axiosError?.response?.data?.message || "";
    const isContentPolicy =
      axiosError?.response?.status === 400 &&
      (errorMessage.toLowerCase().includes("content policy") ||
        errorMessage.toLowerCase().includes("blocked") ||
        errorMessage.toLowerCase().includes("copyright"));

    chatMessages.value.push({
      type: "agent",
      content: isContentPolicy ? trans.errors.content_policy : trans.errors.modify_failed_chat,
      isError: true,
    });
    iterationPrompt.value = "";
    console.error("Failed to modify banner:", error);
  },
});

const {
  data: history,
  refetch: refetchHistory,
  isLoading: isHistoryLoading,
} = useQuery<Array<string>>({
  queryKey: ["bannersHistory"],
  queryFn: async () => {
    return (await getHistory()) || [];
  },
  enabled: computed(() => props.isOpen),
});

const {
  data: productSearchData,
  fetchNextPage: fetchNextProductPage,
  hasNextPage: hasNextProductPage,
  isLoading: isProductsLoading,
  isFetchingNextPage: isProductsFetchingNextPage,
} = useInfiniteQuery({
  queryKey: ["aiBannerProductSearch", productSearchQuery],
  queryFn: async ({ pageParam }) => {
    return getDataScienceSearchProducts(productSearchQuery.value, pageParam);
  },
  initialPageParam: null,
  getNextPageParam: (lastPage: DTODataScienceSearchProductsResponse) => lastPage.searchAfter ?? undefined,
  enabled: computed(() => productSearchQuery.value.trim().length > 0),
});

const searchProducts = computed<AiBannerProduct[]>(() => {
  if (!productSearchData.value?.pages) return [];
  return productSearchData.value.pages.flatMap((page: DTODataScienceSearchProductsResponse) =>
    page.hits.map(
      (hit): AiBannerProduct => ({
        trackerProductId: hit.trackerProductId,
        appIdProductId: `${window.EDRONE_CONTEXT.appId}_${hit.productId}`,
        price: hit.productPrice ?? 0,
        title: hit.productTitle ?? "",
        img: hit.productImageResizedProxy,
      })
    )
  );
});

const { data: bestsellersData, isLoading: isBestsellersLoading } = useQuery({
  queryKey: ["aiBannerBestsellers"],
  queryFn: async () => {
    return getTrackerCategoryProductsTableV2({
      trackerProductCategoryId: null,
      offset: 0,
      limit: 10,
      sortBy: TRACKER_CATEGORY_PRODUCT_TABLE_SORT_KEY.TOTAL_ORDERED,
      sortOrder: SORT_DIRECTION.DESC,
    });
  },
  staleTime: 1000 * 60 * 5,
});

const bestsellers = computed<AiBannerProduct[]>(() => {
  if (!bestsellersData.value?.data) return [];
  return bestsellersData.value.data.map((product) => ({
    trackerProductId: product.trackerProductId ?? 0,
    appIdProductId: product.appIdProductId ?? "",
    price: product.price ?? 0,
    title: product.productName ?? "",
    img: product.imageUrl ?? null,
  }));
});

const isMaxProductsSelected = computed(() => activeProductsSelection.value.length >= 10);

useInfiniteScroll(
  productScrollContainer,
  () => {
    if (hasNextProductPage.value && !isProductsFetchingNextPage.value) {
      fetchNextProductPage();
    }
  },
  { distance: 100 }
);

const handleProductSearchInput = debounce((value: string) => {
  productSearchQuery.value = value;
}, 350);

const activeProductsSelection = computed(() => {
  return isModifyProductsView.value ? modifySelectedProducts.value : selectedProducts.value;
});

const isProductSelected = (product: AiBannerProduct): boolean => {
  return activeProductsSelection.value.some((p) => p.trackerProductId === product.trackerProductId);
};

const handleProductToggle = (product: AiBannerProduct) => {
  if (isModifyProductsView.value) {
    if (isProductSelected(product)) {
      modifySelectedProducts.value = modifySelectedProducts.value.filter(
        (p) => p.trackerProductId !== product.trackerProductId
      );
    } else if (modifySelectedProducts.value.length < 10) {
      modifySelectedProducts.value = [...modifySelectedProducts.value, product];
    }
  } else {
    if (isProductSelected(product)) {
      selectedProducts.value = selectedProducts.value.filter((p) => p.trackerProductId !== product.trackerProductId);
    } else if (!isMaxProductsSelected.value) {
      selectedProducts.value = [...selectedProducts.value, product];
    }
  }
};

const handleCloseProductsView = () => {
  if (isModifyProductsView.value) {
    isModifyProductsView.value = false;
  }
  currentView.value = "prompt";
};

const handleUpgradePlan = () => {
  window.location.href = "/app/account/billing";
};

const handleSubmitForm = () => {
  if (!prompt.value.trim() || isLimitReached.value) return;
  chatMessages.value.push({
    type: "user",
    content: prompt.value,
    imageUrl: preview.value || undefined,
    products: selectedProducts.value.map((p) => ({ title: p.title, img: p.img || undefined })),
  });

  lastUsedPrompt.value = prompt.value;
  lastUsedModel.value = selectedModel.value;
  lastUsedAttachmentUrl.value = attachmentUrl.value;
  savePromptToHistory(prompt.value);
  loadPromptHistory();
  chatId.value = null;

  const sizeConfig = BANNER_SIZES.find((s) => s.id === selectedSize.value) || BANNER_SIZES[0];
  generate({
    prompt: prompt.value,
    modelId: selectedModel.value,
    attachment: attachmentFile.value,
    attachmentUrl: attachmentUrl.value,
    pageUrl: window.location.href,
    productIds: selectedProducts.value.map((p) => p.trackerProductId),
    width: sizeConfig.width,
    height: sizeConfig.height,
    skipAnalysis: selectedAnalysisMode.value === "disabled",
  });

  prompt.value = "";
};

const handleStartOver = () => {
  chatMessages.value = [];
  attachmentFile.value = null;
  attachmentPreview.value = null;
  attachmentUrl.value = null;
  modifyAttachmentFile.value = null;
  modifyAttachmentPreview.value = null;
  modifyAttachmentUrl.value = null;
  selectedModel.value = 2;
  iterationPrompt.value = "";
  chatId.value = null;
  prompt.value = getRandomPrompt();
  selectedProducts.value = [];
  modifySelectedProducts.value = [];
  isModifyProductsView.value = false;
  currentView.value = "prompt";
  productSearchQuery.value = "";
};

const handleRemoveProduct = (product: AiBannerProduct) => {
  selectedProducts.value = selectedProducts.value.filter((p) => p.trackerProductId !== product.trackerProductId);
};

const handleRemoveModifyProduct = (product: AiBannerProduct) => {
  modifySelectedProducts.value = modifySelectedProducts.value.filter(
    (p) => p.trackerProductId !== product.trackerProductId
  );
};

const openModifyProductsView = () => {
  isModifyProductsView.value = true;
  currentView.value = "products";
};

const triggerModifyFileInput = () => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";
  input.onchange = handleModifyFileSelect;
  input.click();
};

const handleModifyFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (file) {
    processModifyFile(file);
  }
};

const processModifyFile = (file: File) => {
  if (!file.type.startsWith("image/")) {
    window.parent.showToast(trans.errors.invalid_file, "error");
    return;
  }

  const maxSize = 2 * 1024 * 1024;
  if (file.size > maxSize) {
    window.parent.showToast(trans.errors.file_too_large, "error");
    return;
  }

  modifyAttachmentFile.value = file;

  const reader = new FileReader();
  reader.onload = (e) => {
    modifyAttachmentPreview.value = e.target?.result as string;
  };
  reader.readAsDataURL(file);
};

const removeModifyAttachment = () => {
  modifyAttachmentFile.value = null;
  modifyAttachmentPreview.value = null;
  modifyAttachmentUrl.value = null;
};

const handleIterate = () => {
  if (!iterationPrompt.value.trim() || !chatId.value || isLimitReached.value) return;

  chatMessages.value.push({
    type: "user",
    content: iterationPrompt.value,
    imageUrl: modifyPreview.value || undefined,
    products: modifySelectedProducts.value.map((p) => ({ title: p.title, img: p.img || undefined })),
  });

  modify({
    prompt: iterationPrompt.value,
    chatId: chatId.value,
    pageUrl: window.location.href,
    attachment: modifyAttachmentFile.value,
    attachmentUrl: modifyAttachmentUrl.value,
    productIds: modifySelectedProducts.value.map((p) => p.trackerProductId),
  });

  modifyAttachmentFile.value = null;
  modifyAttachmentPreview.value = null;
  modifyAttachmentUrl.value = null;
  modifySelectedProducts.value = [];
};

const handleIterateFromMessage = (message: string) => {
  iterationPrompt.value = message;
  handleIterate();
};

const triggerFileInput = () => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";
  input.onchange = handleFileSelect;
  input.click();
};

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (file) {
    processFile(file);
  }
};

const processFile = (file: File) => {
  if (!file.type.startsWith("image/")) {
    window.parent.showToast(trans.errors.invalid_file, "error");
    return;
  }

  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) {
    window.parent.showToast(trans.errors.file_too_large, "error");
    return;
  }

  attachmentFile.value = file;

  const reader = new FileReader();
  reader.onload = (e) => {
    attachmentPreview.value = e.target?.result as string;
  };
  reader.readAsDataURL(file);
};

const removeAttachment = () => {
  attachmentFile.value = null;
  attachmentPreview.value = null;
  attachmentUrl.value = null;
};

const handleSelectBanner = (url: string) => {
  if (props.downloadOnSelect) {
    downloadImage(url);
  } else {
    store.setLastGeneratedBannerUrl(url);
  }
};

const downloadImage = (url: string) => {
  const link = document.createElement("a");
  link.href = "/ai-banner/download?url=" + url;
  link.download = `banner-${Date.now()}.png`;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const updateScrollArrows = () => {
  if (!historyScrollRef.value) return;
  const { scrollLeft, scrollWidth, clientWidth } = historyScrollRef.value;
  canScrollLeft.value = scrollLeft > 0;
  canScrollRight.value = scrollLeft < scrollWidth - clientWidth - 1;
};

const scrollHistory = (direction: "left" | "right") => {
  if (!historyScrollRef.value) return;
  const scrollAmount = 300;
  historyScrollRef.value.scrollBy({
    left: direction === "left" ? -scrollAmount : scrollAmount,
    behavior: "smooth",
  });
  setTimeout(updateScrollArrows, 350);
};

const loadPromptHistory = () => {
  promptHistory.value = getPromptHistory();
};

const selectPromptFromHistory = (historyItem: PromptHistoryItem) => {
  prompt.value = historyItem.prompt;
  showHistoryDropdown.value = false;
};

const toggleHistoryDropdown = () => {
  loadPromptHistory();
  showHistoryDropdown.value = !showHistoryDropdown.value;
};

loadPromptHistory();

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (showHistoryDropdown.value && historyDropdownRef.value && !historyDropdownRef.value.contains(target)) {
    showHistoryDropdown.value = false;
  }
  if (showSizeDropdown.value && sizeDropdownRef.value && !sizeDropdownRef.value.contains(target)) {
    showSizeDropdown.value = false;
  }
  if (showModelDropdown.value && modelDropdownRef.value && !modelDropdownRef.value.contains(target)) {
    showModelDropdown.value = false;
  }
  if (showAnalysisDropdown.value && analysisDropdownRef.value && !analysisDropdownRef.value.contains(target)) {
    showAnalysisDropdown.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style scoped>
.ai-banner-tooltip-wrapper {
  position: relative;
  display: inline-block;
}

.ai-banner-tooltip {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 8px;
  padding: 8px;
  background-color: white;
  color: inherit;
  font-size: 12px;
  line-height: 1.4;
  border-radius: 4px;
  white-space: normal;
  width: max-content;
  max-width: 280px;
  z-index: 999999999;
  box-shadow: 4px 4px 24px rgba(0, 0, 0, 0.22);
  transition: opacity 0.2s ease, visibility 0.2s ease;
  text-wrap: balance;
}

.ai-banner-tooltip-wrapper:hover .ai-banner-tooltip {
  visibility: visible;
  opacity: 1;
}

.ai-banner-history-scroll {
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;
}

.ai-banner-history-scroll::-webkit-scrollbar {
  height: 6px;
}

.ai-banner-history-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.ai-banner-history-scroll::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 3px;
}

.ai-banner-history-scroll::-webkit-scrollbar-thumb:hover {
  background-color: #9ca3af;
}
</style>
